import React from 'react';
import { Calendar, Mail, Phone, MapPin, Building2, Users, TrendingUp, Truck, Globe, Award } from 'lucide-react';

const Home = () => {
  const calendlyUrl = "https://calendly.com/monikasheridan";

  const handleBookConsultation = () => {
    window.open(calendlyUrl, '_blank', 'width=800,height=800');
  };

  return (
    <div className="landing-page">
      {/* Navigation Header */}
      <nav className="nav-header">
        <div className="nav-logo">
          <span className="logo-text">Monika Sheridan</span>
        </div>
        <div className="nav-links">
          <a href="#about" className="nav-link">About</a>
          <a href="#services" className="nav-link">Services</a>
          <a href="#achievements" className="nav-link">Achievements</a>
          <a href="#contact" className="nav-link">Contact</a>
        </div>
        <button onClick={handleBookConsultation} className="btn-primary nav-cta">
          <Calendar className="w-4 h-4" />
          <span>Book Consultation</span>
        </button>
      </nav>

      {/* Hero Section */}
      <section className="hero-section" id="hero">
        <div className="hero-content">
          <div className="hero-image-container">
            <img
              src="monika-profile.png"
              alt="Monika Sheridan - Operations & Supply Chain Consultant"
              className="hero-image" />

          </div>
          <h1 className="hero-title">
            Streamline Your Operations.<br />
            Scale Your Business.
          </h1>
          <p className="hero-subtitle">
            Helping international trading and logistics businesses cut costs, optimize supply chains, and build high-performing teams.
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">20+</div>
              <div className="stat-label">Years Experience</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">5</div>
              <div className="stat-label">Global Locations</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">4</div>
              <div className="stat-label">Industry Expertise</div>
            </div>
          </div>
          <div className="hero-cta">
            <button onClick={handleBookConsultation} className="btn-primary btn-large">
              <Calendar className="w-5 h-5" />
              <span>Book Free Consultation</span>
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-container" id="about">
        <div className="content-wrapper">
          <h2 className="section-title">About Monika</h2>
          <div className="about-content">
            <p className="body-large">Based in Warsaw and working globally, she specializes in building scalable operation systems for businesses in the commodity trading, import-export, and manufacturing sectors.

            </p>
            <p className="body-medium">Known as a trusted problem-solver and approachable leader, Monika has built and led multinational teams across Europe, Africa, and Asia. She delivers practical, intuitive solutions that bridge departmental silos and align operational needs with business objectives.

            </p>
            <div className="differentiators">
              <div className="diff-item">
                <Award className="w-6 h-6 text-accent" />
                <div>
                  <h4 className="diff-title">Proven Track Record</h4>
                  <p className="diff-text">Leadership roles at global commodity and trading companies in UK and Netherlands</p>
                </div>
              </div>
              <div className="diff-item">
                <Globe className="w-6 h-6 text-accent" />
                <div>
                  <h4 className="diff-title">International Expertise</h4>
                  <p className="diff-text">Managed operations across multiple continents with diverse cultural contexts</p>
                </div>
              </div>
              <div className="diff-item">
                <Users className="w-6 h-6 text-accent" />
                <div>
                  <h4 className="diff-title">Team Builder</h4>
                  <p className="diff-text">Builds high-performing, motivated multinational teams from the ground up</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-container section-bg" id="services">
        <div className="content-wrapper">
          <h2 className="section-title">How I Can Help</h2>
          <p className="section-subtitle">
            Tailored consulting services designed to solve your operational challenges
          </p>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon"><TrendingUp className="w-8 h-8" /></div>
              <h3 className="service-title">Operational Excellence</h3>
              <p className="service-description">Streamline workflows, eliminate inefficiencies, and build scalable systems that grow with your business.</p>
              <div className="service-ideal">
                <span className="ideal-label">Ideal for:</span>
                <p className="ideal-text">Companies experiencing rapid growth or operational bottlenecks</p>
              </div>
            </div>
            
            <div className="service-card">
              <div className="service-icon"><Truck className="w-8 h-8" /></div>
              <h3 className="service-title">Supply Chain & Logistics Optimization</h3>
              <p className="service-description">Design end-to-end supply chain solutions that reduce costs, improve delivery times, and enhance customer satisfaction.</p>
              <div className="service-ideal">
                <span className="ideal-label">Ideal for:</span>
                <p className="ideal-text">Trading and logistics companies seeking competitive advantage</p>
              </div>
            </div>
            
            <div className="service-card">
              <div className="service-icon"><Users className="w-8 h-8" /></div>
              <h3 className="service-title">Team Building & Leadership Development</h3>
              <p className="service-description">Build high-performing multinational teams and develop leadership capabilities across your organization.</p>
              <div className="service-ideal">
                <span className="ideal-label">Ideal for:</span>
                <p className="ideal-text">Organizations expanding internationally or restructuring operations</p>
              </div>
            </div>
            
            <div className="service-card">
              <div className="service-icon"><Building2 className="w-8 h-8" /></div>
              <h3 className="service-title">Process Design & Implementation</h3>
              <p className="service-description">Create and implement efficient processes from scratch or redesign existing systems for better performance.</p>
              <div className="service-ideal">
                <span className="ideal-label">Ideal for:</span>
                <p className="ideal-text">Businesses launching new operations or modernizing workflows</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who I Work With */}
      <section className="section-container" id="clients">
        <div className="content-wrapper">
          <h2 className="section-title">Who I Work With</h2>
          <div className="client-profiles">
            <div className="client-profile-card">
              <Building2 className="w-5 h-5 text-accent" />
              <p className="profile-text">Commodity Trading Houses, SMEs, Producers</p>
            </div>
            <div className="client-profile-card">
              <Building2 className="w-5 h-5 text-accent" />
              <p className="profile-text">Import/export businesses needing operational structure</p>
            </div>
            <div className="client-profile-card">
              <Building2 className="w-5 h-5 text-accent" />
              <p className="profile-text">Logistics-heavy organizations (shipping, supply chain, warehousing)</p>
            </div>
            <div className="client-profile-card">
              <Building2 className="w-5 h-5 text-accent" />
              <p className="profile-text">Leadership teams requiring operational excellence expertise</p>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="section-container section-bg" id="achievements">
        <div className="content-wrapper">
          <h2 className="section-title">Selected Achievements</h2>
          <div className="achievements-grid">
            <div className="achievement-card">
              <div className="achievement-number">01</div>
              <p className="achievement-text">Designed and delivered pioneering project supplying fully traceable green coffee directly from farm to factory</p>
            </div>
            <div className="achievement-card">
              <div className="achievement-number">02</div>
              <p className="achievement-text">Built high-performing multinational team managing operations across several global locations.</p>
            </div>
            <div className="achievement-card">
              <div className="achievement-number">03</div>
              <p className="achievement-text">Delivered significant cost savings through improved supply chain, processes, and shipping solutions while enhancing customer experience</p>
            </div>
            <div className="achievement-card">
              <div className="achievement-number">04</div>
              <p className="achievement-text">Successfully implemented new systems and processes across international operations</p>
            </div>
          </div>
        </div>
      </section>

      {/* Skills & Expertise */}
      <section className="section-container" id="skills">
        <div className="content-wrapper">
          <h2 className="section-title">Skills & Expertise</h2>
          <div className="skills-grid">
            <div className="skill-category">
              <h3 className="skill-category-title">Operational Excellence</h3>
              <ul className="skill-list">
                <li className="skill-item">Process Improvement</li>
                <li className="skill-item">System Implementation</li>
                <li className="skill-item">Workflow Optimization</li>
              </ul>
            </div>
            
            <div className="skill-category">
              <h3 className="skill-category-title">Leadership</h3>
              <ul className="skill-list">
                <li className="skill-item">Multinational Team Leadership</li>
                <li className="skill-item">Team Development</li>
                <li className="skill-item">Crisis Management</li>
              </ul>
            </div>
            
            <div className="skill-category">
              <h3 className="skill-category-title">Supply Chain & Logistics</h3>
              <ul className="skill-list">
                <li className="skill-item">Global Supply Chain Management</li>
                <li className="skill-item">Shipping & Freight</li>
                <li className="skill-item">Inventory Optimization</li>
              </ul>
            </div>
            
            <div className="skill-category">
              <h3 className="skill-category-title">Strategic Planning</h3>
              <ul className="skill-list">
                <li className="skill-item">Complex Project Management</li>
                <li className="skill-item">Client Relations</li>
                <li className="skill-item">Risk Management</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact/CTA Section */}
      <section className="cta-section" id="contact">
        <div className="content-wrapper">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Transform Your Operations?</h2>
            <p className="cta-subtitle">
              Let's discuss how I can help streamline your business, reduce costs, and build the team you need to succeed in international markets.
            </p>
            <button onClick={handleBookConsultation} className="btn-primary btn-large cta-button">
              <Calendar className="w-5 h-5" />
              <span>Schedule Your Free Consultation</span>
            </button>
            <div className="contact-details">
              <div className="contact-item">
                <MapPin className="w-5 h-5" />
                <span className="contact-text">Based in Warsaw, Poland (Working Internationally)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="content-wrapper">
          <div className="footer-content">
            <div className="footer-left">
              <p className="footer-name">Operations & Supply Chain Consultant</p>
              <p className="footer-tagline">Member of the Institute of Chartered Shipbrokers.</p>
            </div>
            <div className="footer-right">
              <p className="footer-credentials"></p>
              <p className="footer-copyright">© 2026 Monika Sheridan. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;
